/**
 * scEditor.ts
 * Secured Communications
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import { Editor, Mention } from "ckeditor5";

export interface AccountMemberMention {
  id: string,
  userID: string,
  firstname: string,
  lastname: string,
  email: string,
  level: string,

  [name: string]: unknown
}

export interface EmojiMention {
  id: string
  _id: string
  isEmoji: true
  name: string
  text: string
}

function isEmojiMention(mention: AccountMemberMention | EmojiMention): mention is EmojiMention {
  return (mention as EmojiMention).isEmoji === true;
}


export function MentionCustomization(editor: Editor): any {
  // The upcast converter will convert <a class="mention" href="" data-user-id="">
  // elements to the model "mention" attribute.
  editor.conversion.for("upcast").elementToAttribute({
    view: {
      name: "span",
      //key: "data-member-id",
      classes: "mention",
      attributes: {
        "data-member-id": true,
        "data-tag": true,
        "data-fname": true,
        "data-lname": true,
        "data-email": true,
        "data-level": true
      }
    },
    model: {
      key: "mention",
      value: (viewItem: any) => {
        // The mention feature expects that the mention attribute value
        // in the model is a plain object with a set of additional attributes.
        // In order to create a proper object, use the toMentionAttribute helper method:
        const mentionAttribute = (editor.plugins.get("Mention") as Mention).toMentionAttribute(viewItem, {
          firstname: viewItem.getAttribute("data-fname"),
          lastname: viewItem.getAttribute("data-lname"),
          email: viewItem.getAttribute("data-email"),
          userID: viewItem.getAttribute("data-member-id"),
          level: viewItem.getAttribute("data-level")
        } as AccountMemberMention);

        return mentionAttribute as any;
      }
    },
    converterPriority: "high"
  });

  editor.conversion.for("downcast").attributeToElement({
    model: "mention",
    view: (membership: AccountMemberMention | EmojiMention, { writer }: any) => {
      if (!membership) {
        return;
      }
      if (isEmojiMention(membership)) {
        return writer.createAttributeElement("span", {
          class: "emoji",
          "data-id": membership._id
        }, {
          priority: 20,
          id: membership.id
        });
      }

      return writer.createAttributeElement("span", {
        class: "mention",
        "data-member-id": membership.userID,
        "data-fname": membership.firstname,
        "data-lname": membership.lastname,
        "data-email": membership.email,
        "data-level": membership.level,
        "data-tag": membership.userID + "--" + membership.level + "--" + membership.team + "--" + membership.teamID
      }, {
        // Make mention attribute to be wrapped by other attribute elements.
        priority: 20,
        // Prevent merging mentions together.
        id: membership.id
      });
    },
    converterPriority: "high"
  });
}

export function ImageCustomization(editor: Editor): any {
  editor.conversion.attributeToAttribute({
    model: "width",
    view: "data-width"
  });
  editor.conversion.attributeToAttribute({
    model: "height",
    view: "data-height"
  });
}
